import noresponse from "@/../../hypetrain/static/img/no-response-circle.svg";
import ongoing from "@/../../hypetrain/static/img/ongoing-circle.svg";
import actionItemsIcon from "@/../../hypetrain/static/img/program/action-items.svg";
import aiRoleplayIcon from "@/../../hypetrain/static/img/program/ai-roleplay.svg";
import announcementIcon from "@/../../hypetrain/static/img/program/announcement.svg";
import articleIcon from "@/../../hypetrain/static/img/program/article-icon.svg";
import oneOneMeeting from "@/../../hypetrain/static/img/program/components-logo.svg";
import groupMeeting from "@/../../hypetrain/static/img/program/meeting.svg";
import surveyIcon from "@/../../hypetrain/static/img/program/survey.svg";
import videoIcon from "@/../../hypetrain/static/img/program/video-icon.svg";
import { Colors, Icon } from "@blueprintjs/core";

import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { ComponentTypeEnum, ComponentStatus } from "@/graphql";
import { LeadComponent, ParticipantComponent } from "@/types/programs";

export const PROGRAM_COMPONENT_ICONS: Partial<
  Record<ComponentTypeEnum, { icon: string; classIcon: string }>
> = {
  [ComponentTypeEnum.Announcement]: {
    icon: announcementIcon,
    classIcon: "volume-up",
  },
  [ComponentTypeEnum.ActionItem]: {
    icon: actionItemsIcon,
    classIcon: "form",
  },
  [ComponentTypeEnum.Survey]: {
    icon: surveyIcon,
    classIcon: "grouped-bar-chart",
  },
  [ComponentTypeEnum.AiRoleplay]: {
    icon: aiRoleplayIcon,
    classIcon: "chat",
  },
  [ComponentTypeEnum.Video]: {
    icon: videoIcon,
    classIcon: "video",
  },
  [ComponentTypeEnum.Article]: {
    icon: articleIcon,
    classIcon: "manual",
  },
  [ComponentTypeEnum.GroupMeeting]: {
    icon: groupMeeting,
    classIcon: "manual",
  },
  [ComponentTypeEnum.OneOneCoachingMaster]: {
    icon: oneOneMeeting,
    classIcon: "unresolve",
  },
  [ComponentTypeEnum.OneOneCoachingSession]: {
    icon: oneOneMeeting,
    classIcon: "unresolve",
  },
  [ComponentTypeEnum.OneOneCoachingIntro]: {
    icon: oneOneMeeting,
    classIcon: "unresolve",
  },
};

export const COMPONENT_STATUS_CONFIG: Partial<
  Record<
    ComponentStatus,
    {
      color: string;
      backgroundColor: string;
      icon: React.ReactNode;
      text: string;
    }
  >
> = {
  [ComponentStatus.Upcoming]: {
    color: Colors.ORANGE3,
    backgroundColor: "#FBF2E9",
    icon: <Icon icon="circle" color="#BF7326" size={14} />,
    text: "Upcoming",
  },
  [ComponentStatus.Ongoing]: {
    color: Colors.BLUE3,
    backgroundColor: "#E9F1FB",
    icon: <EmbedSvg src={ongoing} height={14} width={14} lazy={false} />,
    text: "Ongoing",
  },
  [ComponentStatus.Soon]: {
    color: Colors.BLUE3,
    backgroundColor: "#E9F1FB",
    icon: <EmbedSvg src={ongoing} height={14} width={14} lazy={false} />,
    text: "Soon",
  },
  [ComponentStatus.Completed]: {
    color: Colors.GREEN3,
    backgroundColor: "#E6F5EF",
    icon: <Icon icon="tick-circle" size={14} color="#0d8050" />,
    text: "Completed",
  },
  [ComponentStatus.InDesign]: {
    color: "#778397",
    backgroundColor: "#F2F4F8",
    icon: <Icon icon="wrench" size={14} color="#778397" />,
    text: "In Design",
  },
  [ComponentStatus.NoResponse]: {
    color: "#C61D1D",
    icon: <EmbedSvg src={noresponse} height={14} width={14} lazy={false} />,
    text: "No Response",
    backgroundColor: "#F5E6E8",
  },
  [ComponentStatus.Missed]: {
    color: "#C61D1D",
    icon: <Icon icon="cross-circle" size={14} color="#C61D1D" />,
    text: "Missed",
    backgroundColor: "#F5E6E8",
  },
};

export const COMPONENT_DETAILS_PATHS: Partial<
  Record<ComponentTypeEnum, string>
> = {
  [ComponentTypeEnum.ActionItem]: "action-items",
  [ComponentTypeEnum.Survey]: "surveys",
  [ComponentTypeEnum.AiRoleplay]: "ai-roleplays",
  [ComponentTypeEnum.Video]: "videos",
  [ComponentTypeEnum.Article]: "articles",
  [ComponentTypeEnum.Announcement]: "announcement",
  [ComponentTypeEnum.GroupMeeting]: "group-meetings",
  [ComponentTypeEnum.OneOneCoachingMaster]: "one-one-coaching",
  [ComponentTypeEnum.OneOneCoachingIntro]: "one-one-coaching",
  [ComponentTypeEnum.OneOneCoachingSession]: "one-one-coaching",
};

interface GetComponentDatetimeProps {
  component: LeadComponent | ParticipantComponent;
}

export function getComponentDatetime({ component }: GetComponentDatetimeProps) {
  const { targetStartDate, targetDueDate, closedAt, targetEndDate } =
    component || {};

  const startDate = targetStartDate;
  const dueDate = closedAt || targetDueDate;
  const endDate = closedAt || targetEndDate;

  return { startDate, dueDate, endDate };
}

interface GetComponentDetailsPathsProps {
  programId: string;
  type?: ComponentTypeEnum | null;
  tagID?: string | null;
  currentRoute?: string;
}

export function getComponentDetailsPaths({
  programId,
  type,
  tagID = null,
  currentRoute = "",
}: GetComponentDetailsPathsProps) {
  let path = `/programs/${programId}`;

  if (type && COMPONENT_DETAILS_PATHS[type]) {
    path = `${path}/${COMPONENT_DETAILS_PATHS[type]}`;
    // If the path is the same as the current route, return null to not redirect
    if (path === currentRoute) {
      return null;
    }
  }

  if (tagID) {
    path = `${path}${tagID}`;
  }

  return path;
}

interface ScrollWithOffsetProps {
  componentTag: string;
  offset?: number;
}

export function scrollWithOffset({
  componentTag,
  offset = 0,
}: ScrollWithOffsetProps) {
  try {
    const element = document.getElementById(componentTag);
    if (!element) {
      console.warn(`Element with id ${componentTag} not found`);
      return;
    }

    const elementPosition =
      element.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({
      top: elementPosition + offset,
      behavior: "smooth",
    });
  } catch (e) {
    console.error("Error in scrollWithOffset:", e);
  }
}
